import {
  type Dispatch,
  type ForwardedRef,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useImperativeHandle,
} from 'react'
import type { PenToolOverlayHandle } from '@/components/app/editor/tools/pen-tool-overlay'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { duplicateSelection } from '../handlers/duplicate-selection'
import type { CenterCanvasHandle } from '../types/center-canvas-handle'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * The `CenterCanvasHandle` the parent (`home.tsx`) drives the canvas through: zoom and viewport
 * commands, the Edit-menu clipboard/delete/select-all routes, lock/freeze, the edit modes'
 * enter/exit/sync and undo floors, and pen-tool teardown.
 */
export function useCenterCanvasHandle({
  ref,
  internalShapeManagerRef,
  fitWidth,
  zoomToFitSelection,
  centerSpreadForActualSize,
  refitFitSpread,
  zoomInFromToolbar,
  zoomOutFromToolbar,
  centerViewportOnSelectionOrSpread,
  clearUnifiedSelectionWithHistory,
  lockSelectedObjects,
  unlockSelectedObjects,
  freezeSelectedObjects,
  unfreezeSelectedObjects,
  unfreezeAllObjects,
  unlockAllObjects,
  isShapeTextEditing,
  copySelectionToClipboard,
  cutSelectionForActiveMode,
  pasteFromClipboard,
  hasPasteCandidate,
  isTextEditSessionActive,
  deleteSelectionForActiveMode,
  deleteSelectedObjects,
  onDeleteShapesHandled,
  selectAllForActiveTextEdit,
  activeIsolationGroupId,
  getCurrentSelectionSnapshots,
  penToolOverlayRef,
  setPenToolOverlayKey,
  nodeEditShapeIdRef,
  setNodeEditShapeData,
  tryEnterNodeEditMode,
  exitNodeEditMode,
  nodeEditUndoFloorRef,
  cropShapeIdRef,
  setCropShapeData,
  tryEnterCropMode,
  exitCropMode,
  cropUndoFloorRef,
  enterTextOnPathModeById,
  commitAndExitTextOnPathMode,
  resolveTextOnPathTargetId,
  getUndoStackSize,
}: {
  ref: ForwardedRef<CenterCanvasHandle>
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  fitWidth: () => void
  zoomToFitSelection: () => void
  centerSpreadForActualSize: () => void
  refitFitSpread: () => void
  zoomInFromToolbar: () => boolean
  zoomOutFromToolbar: () => boolean
  centerViewportOnSelectionOrSpread: () => void
  clearUnifiedSelectionWithHistory: () => void
  lockSelectedObjects: () => void
  unlockSelectedObjects: () => void
  freezeSelectedObjects: () => void
  unfreezeSelectedObjects: () => void
  unfreezeAllObjects: () => void
  unlockAllObjects: () => void
  isShapeTextEditing: boolean
  copySelectionToClipboard: () => boolean
  cutSelectionForActiveMode: () => boolean
  pasteFromClipboard: (
    inPlace: boolean,
    canvasPoint?: { canvasX: number; canvasY: number } | null,
    offsetPt?: number
  ) => boolean
  hasPasteCandidate: () => boolean
  isTextEditSessionActive: () => boolean
  deleteSelectionForActiveMode: (direction: 'forward' | 'backward') => boolean
  deleteSelectedObjects: () => 'none' | 'blocked' | 'deleted'
  onDeleteShapesHandled: CenterCanvasConfig['onDeleteShapesHandled']
  selectAllForActiveTextEdit: () => boolean
  activeIsolationGroupId: string | null
  getCurrentSelectionSnapshots: CenterCanvasHandle['getSelectionSnapshotsForMenus']
  penToolOverlayRef: MutableRefObject<PenToolOverlayHandle | null>
  setPenToolOverlayKey: Dispatch<SetStateAction<number>>
  nodeEditShapeIdRef: MutableRefObject<string | null>
  setNodeEditShapeData: Dispatch<SetStateAction<ShapeData | null>>
  tryEnterNodeEditMode: (shape: ShapeData) => boolean
  exitNodeEditMode: (exitingShapeId: string | undefined) => void
  nodeEditUndoFloorRef: MutableRefObject<number | null>
  cropShapeIdRef: MutableRefObject<string | null>
  setCropShapeData: Dispatch<SetStateAction<ShapeData | null>>
  tryEnterCropMode: (shape: ShapeData) => boolean
  exitCropMode: () => void
  cropUndoFloorRef: MutableRefObject<number | null>
  enterTextOnPathModeById: (shapeId: string) => boolean
  commitAndExitTextOnPathMode: () => void
  resolveTextOnPathTargetId: () => string | null
  getUndoStackSize: CenterCanvasConfig['getUndoStackSize']
}) {
  // The "unified" (mixed shape+text) selection chrome these five imperative-handle methods
  // once drove can never activate — TextManager no longer renders (Phase 2), so a selection can
  // never contain text boxes. Kept as no-ops (rather than removed) since home.tsx's undo/redo
  // integration still calls them defensively; see the Known Gaps note in larry-frontend-ui/CLAUDE.md.
  const consumePendingUnifiedRotateChromeBefore = useCallback(() => null, [])

  const getUnifiedRotateChromeSnapshot = useCallback(() => null, [])

  const getUnifiedMixedSelectionChromeSizeForPanel = useCallback(() => null, [])

  const restoreUnifiedMultiSelectChrome = useCallback(() => {}, [])

  const resyncUnifiedMultiSelectChromeFromSelection = useCallback(() => {}, [])

  useImperativeHandle(ref, () => {
    /** Edit-menu Paste / Paste in Place: into the live text edit, else the canvas clipboard. */
    const pasteSelectionFor = (inPlace: boolean) => {
      if (isShapeTextEditing) {
        void internalShapeManagerRef.current?.execShapeTextPaste()
        return true
      }

      return pasteFromClipboard(inPlace)
    }

    return {
      fitWidth,
      zoomToFitSelection,
      clearSelection: clearUnifiedSelectionWithHistory,
      clearSelectionWithoutHistory: () =>
        internalShapeManagerRef.current?.clearSelection({ recordHistory: false }),
      lockSelected: lockSelectedObjects,
      unlockSelected: unlockSelectedObjects,
      freezeSelected: freezeSelectedObjects,
      unfreezeSelected: unfreezeSelectedObjects,
      unfreezeAll: unfreezeAllObjects,
      unlockAll: unlockAllObjects,
      copySelection: () => {
        if (isShapeTextEditing) {
          internalShapeManagerRef.current?.execShapeTextCopy()
          return true
        }

        return copySelectionToClipboard()
      },
      // Objects only, like Ctrl/Cmd+D: mid text edit there is no object to duplicate. Reachable
      // mid-edit for the same keepalive reason `deleteSelection` below documents.
      duplicateSelection: () =>
        duplicateSelection({
          isTextEditSessionActive,
          copySelectionToClipboard,
          pasteFromClipboard,
        }),
      // Same mode dispatch the Ctrl+X shortcut uses, so the Edit menu and the keyboard can't
      // disagree about which mode is live (WMPCM-7004).
      cutSelection: cutSelectionForActiveMode,
      pasteSelection: () => pasteSelectionFor(false),
      pasteSelectionInPlace: () => pasteSelectionFor(true),
      hasPasteCandidate,
      // Same mode dispatch the Delete key uses, so the Edit menu and the keyboard can't disagree
      // about which mode is live — the lesson `cutSelection` above already learned (WMPCM-7004).
      // This row is reachable mid-edit because the entire menubar dropdown carries
      // `data-text-edit-keepalive`, so opening it does not end the edit session (WMPCM-7044).
      // 'forward' matches a menu Delete's conventional "Clear" semantics; with a merely collapsed
      // caret and no live in-editable selection the editor no-ops rather than eating a character.
      deleteSelection: () => {
        if (isTextEditSessionActive()) return deleteSelectionForActiveMode('forward')

        const deleteResult = deleteSelectedObjects()

        if (deleteResult === 'deleted') {
          onDeleteShapesHandled?.()
          return true
        }

        return false
      },
      // Text-edit mode wins and reports it, so `home.tsx`'s object select-all is skipped rather
      // than run afterwards (WMPCM-6867). Reachable mid-edit for the same keepalive reason
      // `deleteSelection` above documents.
      selectAllForActiveMode: () => selectAllForActiveTextEdit(),
      isInGroupIsolationMode: () => activeIsolationGroupId != null,
      getSelectionSnapshotsForMenus: getCurrentSelectionSnapshots,
      centerSpreadForActualSize,
      refitFitSpread,
      zoomInFromToolbar,
      zoomOutFromToolbar,
      centerViewportOnSelectionOrSpread,
      consumePendingUnifiedRotateChromeBefore,
      getUnifiedRotateChromeSnapshot,
      getUnifiedMixedSelectionChromeSizeForPanel,
      restoreUnifiedMultiSelectChrome,
      resyncUnifiedMultiSelectChromeFromSelection,
      cancelPenToolDrawing: () => {
        // Synchronously commit any in-progress drawing so the shape is enqueued
        // in ChangeQueue before the caller's flushQueue() drains it (page navigation).
        penToolOverlayRef.current?.commitAndDiscard()
        setPenToolOverlayKey((k) => k + 1)
      },
      syncNodeEditShapeFromManager: () => {
        const editingId = nodeEditShapeIdRef.current

        if (!editingId) return
        const liveShape = internalShapeManagerRef.current
          ?.getShapes()
          .find((s) => s.id === editingId)

        if (liveShape && liveShape.type === 'path' && liveShape.path) {
          setNodeEditShapeData((prev) => (prev ? liveShape : prev))
        } else {
          // Shape removed or is no longer a pen path (e.g. undo of convertToPath) — exit.
          exitNodeEditMode(editingId ?? undefined)
        }
      },
      syncCropShapeFromManager: () => {
        const editingId = cropShapeIdRef.current

        if (!editingId) return
        const liveShape = internalShapeManagerRef.current
          ?.getShapes()
          .find((s) => s.id === editingId)

        if (liveShape && liveShape.photoFill) {
          setCropShapeData((prev) => (prev ? liveShape : prev))
        } else {
          // Shape removed or no longer has a photo fill — exit crop mode.
          exitCropMode()
        }
      },
      enterNodeEditMode: (shape: ShapeData) => {
        return tryEnterNodeEditMode(shape)
      },
      exitNodeEditMode: () => {
        const exitingShapeId = nodeEditShapeIdRef.current

        exitNodeEditMode(exitingShapeId ?? undefined)

        // Mirrors Escape/Enter's own exit (both reselect the shape) — without this, a caller
        // that reads the selection right after calling this (e.g. `handleActivateTool`'s
        // "auto-enter shape text edit if exactly one eligible shape is selected" shortcut) sees
        // an empty selection, since node-edit mode clears it on entry and nothing else restores
        // it. Concretely: activating the Type tool right after converting text to a shape
        // (which auto-enters node-edit mode on the result) exited the stale mode correctly but
        // left nothing selected, so the shortcut silently no-op'ed instead of opening the caret.
        if (exitingShapeId) {
          internalShapeManagerRef.current?.selectShapes([exitingShapeId])
        }

        return exitingShapeId ?? undefined
      },
      enterCropMode: (shape: ShapeData) => {
        return tryEnterCropMode(shape)
      },
      exitCropMode,
      enterTextOnPathMode: (shapeId?: string) => {
        const resolved = shapeId ?? resolveTextOnPathTargetId()

        return resolved == null ? false : enterTextOnPathModeById(resolved)
      },
      // Commits, like every other exit — no caller can silently drop the draft.
      exitTextOnPathMode: commitAndExitTextOnPathMode,
      getTextOnPathTargetId: resolveTextOnPathTargetId,
      isCropUndoAtFloor: () => {
        if (cropShapeIdRef.current == null) return false
        if (cropUndoFloorRef.current == null) return false

        return (getUndoStackSize?.() ?? 0) <= cropUndoFloorRef.current
      },
      isNodeEditUndoAtFloor: () => {
        if (nodeEditShapeIdRef.current == null) return false
        if (nodeEditUndoFloorRef.current == null) return false

        return (getUndoStackSize?.() ?? 0) <= nodeEditUndoFloorRef.current
      },
    }
  }, [
    centerSpreadForActualSize,
    fitWidth,
    refitFitSpread,
    clearUnifiedSelectionWithHistory,
    lockSelectedObjects,
    unlockSelectedObjects,
    freezeSelectedObjects,
    unfreezeSelectedObjects,
    unfreezeAllObjects,
    unlockAllObjects,
    isShapeTextEditing,
    copySelectionToClipboard,
    cutSelectionForActiveMode,
    isTextEditSessionActive,
    deleteSelectionForActiveMode,
    selectAllForActiveTextEdit,
    pasteFromClipboard,
    hasPasteCandidate,
    deleteSelectedObjects,
    onDeleteShapesHandled,
    getCurrentSelectionSnapshots,
    activeIsolationGroupId,
    zoomInFromToolbar,
    zoomOutFromToolbar,
    zoomToFitSelection,
    centerViewportOnSelectionOrSpread,
    consumePendingUnifiedRotateChromeBefore,
    getUnifiedRotateChromeSnapshot,
    getUnifiedMixedSelectionChromeSizeForPanel,
    restoreUnifiedMultiSelectChrome,
    resyncUnifiedMultiSelectChromeFromSelection,
    tryEnterNodeEditMode,
    exitNodeEditMode,
    tryEnterCropMode,
    exitCropMode,
    enterTextOnPathModeById,
    commitAndExitTextOnPathMode,
    resolveTextOnPathTargetId,
    getUndoStackSize,
    cropShapeIdRef,
    cropUndoFloorRef,
    nodeEditShapeIdRef,
    nodeEditUndoFloorRef,
    penToolOverlayRef,
    setCropShapeData,
    setNodeEditShapeData,
    setPenToolOverlayKey,
    internalShapeManagerRef,
  ])
}
